import { LitElement, html } from "lit";
import { customElement, property } from "lit/decorators.js";
import { App } from "../model/app";
import { Chart } from "chart.js/auto";

@customElement("app-ranks-chart-component")
export class AppRanksChartComponent extends LitElement {
    @property({ type: Object }) app: App | undefined;

    createRenderRoot() {
        return this;
    }

    firstUpdated() {
        const app = this.app;
        if (!app) return;

        const ctx = document.getElementById("appRanksChart") as HTMLCanvasElement;
        if (!ctx) return;

        // Get the primary color from CSS variable
        const primaryColor = getComputedStyle(document.documentElement).getPropertyValue("--color-primary").trim();

        const baseColor = getComputedStyle(document.documentElement).getPropertyValue("--color-base-200").trim();

        // Destroy existing chart if it exists
        const existingChart = Chart.getChart(ctx);
        if (existingChart) {
            existingChart.destroy();
        }

        // Prepare data for the chart
        const data = {
            labels: app.ranks?.map((rank) => new Date(rank.created ?? new Date()).toLocaleDateString()) || [],
            datasets: [
                {
                    label: "",
                    data: app.ranks?.map((rank) => rank.position) || [],
                    backgroundColor: primaryColor,
                    borderColor: primaryColor,
                    tension: 0.1,
                    fill: false,
                    pointRadius: 6,
                    pointHoverRadius: 8,
                    borderWidth: 6,
                },
            ],
        };

        // Create the chart
        new Chart(ctx, {
            type: "line",
            data: data,
            options: {
                responsive: true,
                maintainAspectRatio: false,
                animation: false,
                layout: {
                    padding: {
                        top: 10,
                        bottom: 10,
                    },
                },
                scales: {
                    y: {
                        reverse: true, // Lower position is better
                        min: 1,
                        ticks: {
                            stepSize: 1,
                            font: {
                                size: 16,
                            },
                            callback: function (value) {
                                return Number.isInteger(value) ? value : "";
                            },
                        },
                        grid: {
                            drawTicks: true,
                            color: function (context) {
                                return Number.isInteger(context.tick.value) ? baseColor : "transparent";
                            },
                        },
                        title: {
                            display: false,
                        },
                    },
                    x: {
                        ticks: {
                            font: {
                                size: 16,
                            },
                        },
                        title: {
                            display: false,
                        },
                    },
                },
                plugins: {
                    legend: {
                        display: false,
                    },
                    tooltip: {
                        mode: "index",
                        intersect: false,
                        displayColors: false,
                        titleFont: {
                            size: 18,
                            weight: "bold",
                        },
                        bodyFont: {
                            size: 16,
                        },
                        callbacks: {
                            label: function (context) {
                                return `Rank: ${context.parsed.y}`;
                            },
                        },
                    },
                },
            },
        });
    }

    render() {
        return html`
            <div class="flex h-128 w-full">
                <canvas id="appRanksChart"></canvas>
            </div>
        `;
    }
}
